/**
 * HeatmapCanvas — двухслойный рендер тепловой карты для одной страницы
 * (см. PRD §7.5).
 *
 * Слой clicks: каждая точка — радиальный градиент с σ ≈ 25px при ширине
 * 800px (PRD §7.5). На других ширинах σ масштабируется пропорционально.
 *
 * Слой attention: 20 горизонтальных «полос» по 5% высоты страницы;
 * плотность — нормализована к ``attentionMax`` (берётся снаружи, единый
 * масштаб по всему документу).
 *
 * Сам PDF под heatmap'ом сейчас не рисуем — admin показывает страницу
 * как «плашку с номером», корреляция с контентом — через viewer-URL в
 * шапке отправки. Реальный underlay появится, когда добавим эндпоинт
 * admin-просмотра PDF (после MVP).
 *
 * Интерактивность:
 *   - ``onActivate`` — если передан, канвас оборачивается в кликабельную
 *     рамку (Enter/Space/клик). Используется для фокус-режима.
 *   - ``showTooltip`` — при наведении показывает overlay с описанием
 *     того, что под курсором (блок внимания + ближайшие клики).
 */

import { useEffect, useMemo, useRef, useState } from "react";

import type {
  HeatmapAttentionBlock,
  HeatmapClick,
  HeatmapLayer,
} from "../api/analytics";
import { formatMs, pluralRu } from "../lib/format";
import { cn } from "../lib/utils";

interface Props {
  layer: HeatmapLayer;
  pageNumber: number;
  clicks: HeatmapClick[];
  attention: HeatmapAttentionBlock[];
  /** Максимум total_attention_ms по всему документу — для нормализации. */
  attentionMax: number;
  /**
   * Желаемая ширина в CSS-пикселях. Если не передана — канвас занимает
   * 100% ширины родительского контейнера (ResizeObserver). Высота
   * вычисляется из aspectRatio.
   */
  width?: number;
  /** Соотношение height/width страницы. По умолчанию — A4 в портрете. */
  aspectRatio?: number;
  /** Если передан — канвас кликабелен (открыть фокус). */
  onActivate?: () => void;
  /** Показывать overlay-tooltip при наведении. */
  showTooltip?: boolean;
}

const FALLBACK_WIDTH = 320;
const DEFAULT_ASPECT = 1.414; // A4 portrait
const CLICK_SIGMA_AT_800 = 25;
const PAGE_BLOCKS = 20;

interface TooltipState {
  /** Координаты курсора относительно контейнера, в CSS-пикселях. */
  x: number;
  y: number;
  /** Индекс блока внимания (0..19), либо null. */
  blockIndex: number | null;
  blockMs: number;
  blockSamples: number;
  /** Сколько кликов попало в радиус ≈ σ × 3 вокруг курсора. */
  nearbyClicks: number;
}

export function HeatmapCanvas({
  layer,
  pageNumber,
  clicks,
  attention,
  attentionMax,
  width: fixedWidth,
  aspectRatio = DEFAULT_ASPECT,
  onActivate,
  showTooltip = false,
}: Props) {
  const ref = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const [measuredWidth, setMeasuredWidth] = useState<number | null>(null);

  // Если width задан явно — используем его (фокус-модалка). Иначе мерим
  // ширину родителя через ResizeObserver — канвас тянется по гриду.
  useEffect(() => {
    if (fixedWidth !== undefined) return;
    const el = containerRef.current;
    if (el === null) return;
    const measure = () => {
      const w = el.clientWidth;
      if (w > 0) setMeasuredWidth(w);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [fixedWidth]);

  const width = fixedWidth ?? measuredWidth ?? FALLBACK_WIDTH;
  const cssHeight = Math.round(width * aspectRatio);
  const sigma = (width / 800) * CLICK_SIGMA_AT_800;

  const attentionByBlock = useMemo(() => {
    const m = new Map<number, HeatmapAttentionBlock>();
    for (const b of attention) m.set(b.block_index, b);
    return m;
  }, [attention]);

  useEffect(() => {
    const canvas = ref.current;
    if (canvas === null) return;
    const ctx = canvas.getContext("2d");
    if (ctx === null) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(cssHeight * dpr);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${cssHeight}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    // Фон + номер страницы.
    ctx.fillStyle = "#f3f4f6";
    ctx.fillRect(0, 0, width, cssHeight);
    ctx.fillStyle = "#d1d5db";
    ctx.font = `bold ${Math.round(width / 7.5)}px sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(`стр. ${pageNumber}`, width / 2, cssHeight / 2);

    // Attention — оранжевые полосы.
    if (layer === "attention" || layer === "both") {
      const stripeH = cssHeight / PAGE_BLOCKS;
      const safeMax = attentionMax > 0 ? attentionMax : 1;
      for (const block of attention) {
        if (block.block_index < 0 || block.block_index >= PAGE_BLOCKS) continue;
        const density = Math.min(1, block.total_attention_ms / safeMax);
        const alpha = 0.1 + 0.55 * density;
        ctx.fillStyle = `rgba(234, 88, 12, ${alpha})`;
        ctx.fillRect(0, block.block_index * stripeH, width, stripeH);
      }
    }

    // Clicks — красные «капли» с гауссовым градиентом.
    if (layer === "clicks" || layer === "both") {
      const radius = sigma * 3;
      ctx.save();
      for (const click of clicks) {
        const cx = clamp01(click.x) * width;
        const cy = clamp01(click.y) * cssHeight;
        const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
        grad.addColorStop(0, "rgba(220, 38, 38, 0.55)");
        grad.addColorStop(0.6, "rgba(220, 38, 38, 0.18)");
        grad.addColorStop(1, "rgba(220, 38, 38, 0)");
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(cx, cy, radius, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    }
  }, [layer, pageNumber, clicks, attention, attentionMax, width, cssHeight, sigma]);

  function handleMove(e: React.MouseEvent<HTMLDivElement>) {
    if (!showTooltip) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const blockIndex = Math.min(
      PAGE_BLOCKS - 1,
      Math.max(0, Math.floor((y / cssHeight) * PAGE_BLOCKS)),
    );
    const block = attentionByBlock.get(blockIndex);
    const radius = sigma * 3;
    let nearby = 0;
    if (layer === "clicks" || layer === "both") {
      for (const c of clicks) {
        const dx = clamp01(c.x) * width - x;
        const dy = clamp01(c.y) * cssHeight - y;
        if (dx * dx + dy * dy <= radius * radius) nearby += 1;
      }
    }
    setTooltip({
      x,
      y,
      blockIndex: layer !== "clicks" ? blockIndex : null,
      blockMs: block?.total_attention_ms ?? 0,
      blockSamples: block?.sample_count ?? 0,
      nearbyClicks: nearby,
    });
  }

  function handleLeave() {
    setTooltip(null);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (onActivate === undefined) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onActivate();
    }
  }

  const interactive = onActivate !== undefined;

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative block w-full",
        interactive &&
          "cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md",
      )}
      style={fixedWidth !== undefined ? { width: fixedWidth } : undefined}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-label={interactive ? `Открыть страницу ${pageNumber} в фокусе` : undefined}
      onClick={interactive ? onActivate : undefined}
      onKeyDown={interactive ? handleKeyDown : undefined}
      onMouseMove={showTooltip ? handleMove : undefined}
      onMouseLeave={showTooltip ? handleLeave : undefined}
    >
      <canvas
        ref={ref}
        data-testid={`heatmap-page-${pageNumber}`}
        aria-label={`Тепловая карта страницы ${pageNumber}`}
        className="border bg-white shadow-sm block"
      />
      {showTooltip && tooltip !== null && (
        <Tooltip
          tooltip={tooltip}
          containerWidth={width}
          containerHeight={cssHeight}
        />
      )}
    </div>
  );
}

interface TooltipProps {
  tooltip: TooltipState;
  containerWidth: number;
  containerHeight: number;
}

function Tooltip({ tooltip, containerWidth, containerHeight }: TooltipProps) {
  // Прижимаем tooltip к стороне, противоположной курсору, чтобы не вылезал.
  const offset = 12;
  const onRight = tooltip.x < containerWidth / 2;
  const onTop = tooltip.y > containerHeight / 2;

  const lines: string[] = [];
  if (tooltip.blockIndex !== null) {
    const pct = Math.round((tooltip.blockIndex / PAGE_BLOCKS) * 100);
    if (tooltip.blockSamples === 0) {
      lines.push(`Блок ${tooltip.blockIndex + 1}/20 (${pct}–${pct + 5}%): нет данных`);
    } else {
      lines.push(
        `Блок ${tooltip.blockIndex + 1}/20: ${formatMs(tooltip.blockMs)} · ${tooltip.blockSamples} замер${pluralRu(tooltip.blockSamples, "", "а", "ов")}`,
      );
    }
  }
  if (tooltip.nearbyClicks > 0) {
    lines.push(
      `${tooltip.nearbyClicks} клик${pluralRu(tooltip.nearbyClicks, "", "а", "ов")} рядом`,
    );
  } else if (tooltip.blockIndex === null) {
    lines.push("Нет кликов рядом");
  }

  return (
    <div
      role="tooltip"
      data-testid="heatmap-tooltip"
      className={cn(
        "absolute z-10 pointer-events-none rounded-md bg-popover text-popover-foreground border shadow-md text-xs px-2 py-1 whitespace-nowrap",
      )}
      style={{
        left: onRight ? tooltip.x + offset : undefined,
        right: !onRight ? containerWidth - tooltip.x + offset : undefined,
        top: !onTop ? tooltip.y + offset : undefined,
        bottom: onTop ? containerHeight - tooltip.y + offset : undefined,
      }}
    >
      {lines.map((line) => (
        <div key={line}>{line}</div>
      ))}
    </div>
  );
}

function clamp01(v: number): number {
  return Math.min(1, Math.max(0, v));
}
